import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../sdk";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

function formatDate(dateObj) {
  const formattedDay = dateObj.getDate().toString().padStart(2, "0");
  const formattedMonth = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  return `${formattedDay}-${formattedMonth}-${dateObj.getFullYear()}`;
}

export default function UseEventDetails() {
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [eventDetails, setEventDetails] = useState([]);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [options, setOptions] = useState([]);
  const [networkMsg, setnetworkMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [type, setType] = useState("generic");
  
  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    setRowsPerPage(value === "All" ? "All" : +value);
    setPage(0); 
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const [radius, setRadius] = useState(10);
  const [view, setView] = useState("map");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const { authFetch, currentHotel } = useAuth();
  const { hotelId } = useParams();
  
  const today = new Date();
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);
  
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(oneMonthLater);

  const getEventTypes = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/event-categories`,
      });
      const { data } = await get();
      if (data) {
        setEventTypes(data);
      }
    } catch (err) {
      console.error("Error fetching event types:", err);
    }
  }, [authFetch]);

  const getKeywords = useCallback(async () => {
    const body = {
      keyword_type: type,
      cities: type === "generic" ? null : currentHotel?.city,
    };
    try {
      const { get } = await authFetch({
        path: `/keywordsv2`,
      });
      const { data } = await get(`?${new URLSearchParams(body)}`);
      if (data) {
        setOptions(data);
      }
    } catch (err) {
      console.error("Error fetching keywords:", err);
    }
  }, [authFetch, currentHotel, type]);

  const getEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(""); 
      
      const words = selectedKeywords?.map((val) => val.keywordName);
      const isAllSelected = rowsPerPage === "All";

      const body = {
        categoryId: selectedType?.length > 0 ? selectedType.join() : "",
        radius: `0-${radius}`,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        limit: isAllSelected ? "" : rowsPerPage,
        page: isAllSelected ? "" : page, 
        search: searchInput,
        isAll: isAllSelected,
        keyword: words.length > 0 ? words.join() : "",
      };

      // Remove empty parameters
      const cleanedBody = Object.fromEntries(
        Object.entries(body).filter(([_, value]) => value !== "")
      );

      const { get } = await authFetch({
        path: `/hotel/${hotelId}/all-events-v2`,
      });

      const { data, response } = await get(`?${new URLSearchParams(cleanedBody)}`);

      if (response.status === 400) {
        setError(data.messageToUser);
        setnetworkMsg(data.messageToUser);
        setEventDetails([]);
        setCount(0);
      } else if (data) {
        setEventDetails(data); 
        setCount(data?.totalCount);
      }
    } catch (err) {
      console.error("Error fetching event details:", err);
      setError("Failed to fetch events. Please try again.");
      setnetworkMsg("Failed to fetch events. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    selectedKeywords,
    selectedType,
    radius,
    fromDate,
    toDate,
    rowsPerPage,
    page,
    searchInput,
    authFetch,
    hotelId,
  ]);

  const downloadFile = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/download-all-events`,
      });
      
      const words = selectedKeywords?.map((val) => val.keywordName);

      const body = {
        categoryId: selectedType?.length > 0 ? selectedType.join() : "",
        radius: `0-${radius}`,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        keyword: words.length > 0 ? words.join() : "",
        search: searchInput,
      };

      const response = await get(`?${new URLSearchParams(body)}`);
      const blob = await response.response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `All_Events.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
      setnetworkMsg("Failed to download file. Please try again.");
    }
  }, [
    authFetch,
    fromDate,
    hotelId,
    radius,
    searchInput,
    selectedKeywords,
    selectedType,
    toDate,
  ]);

  const handleAdd = useCallback(async () => {
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/all-events`,
      });
      const { data, response } = await post({
        ids: selectedRows,
      });
      
      if (response.status === 200) {
        setnetworkMsg("Event Added Successfully");
        setSelectedRows([]);
        getEventDetails();
      } else {
        setnetworkMsg(data?.messageToUser || "Something Went Wrong");
      }
    } catch (err) {
      console.error("Error adding events:", err);
      setnetworkMsg("Failed to add events. Please try again.");
    }
  }, [authFetch, getEventDetails, hotelId, selectedRows]);

  useEffect(() => {
    getEventTypes();
  }, [getEventTypes]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getEventDetails();
    }, 200);
    return () => clearTimeout(timer);
  }, [getEventDetails]);

  useEffect(() => {
    getKeywords();
  }, [getKeywords]);

  return {
    eventTypes,
    selectedType,
    setSelectedType,
    radius,
    setRadius,
    view,
    setView,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    eventDetails,
    error,
    handleChangePage,
    handleChangeRowsPerPage,
    page,
    setPage,
    rowsPerPage,
    count,
    selectedRows,
    setSelectedRows,
    handleAdd,
    networkMsg,
    setnetworkMsg,
    searchInput,
    setSearchInput,
    options,
    searchKeyword,
    setSearchKeyword,
    selectedKeywords,
    setSelectedKeywords,
    downloadFile,
    loading,
    setType,
    type,
    getEventDetails,
  };
}